import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuditCard from "../components/AuditCard";
import { fmtDate } from "../lib/date";
import { Input } from "../components/ui/input";
import { MagnifyingGlass, ListChecks } from "@phosphor-icons/react";

const ACTIONS = [
  ["", "All actions"], ["create", "Created"], ["update", "Updated"], ["status_change", "Asset status"],
  ["meter_reading", "Meter reading"], ["meter_correction", "Meter correction"],
  ["maintenance_status_change", "Maintenance status"], ["maintenance_complete", "Maintenance completed"],
  ["work_item_add", "Work added"], ["rams_publish", "RAMS published"], ["rams_acknowledge", "RAMS acknowledged"],
  ["tag_issue", "Tag created"], ["tag_revoke", "Tag revoked"], ["return_to_service", "Return to service"],
  ["permission_change", "Permissions"], ["check_complete", "Check completed"],
];

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const diff = Math.round((today - dd) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return fmtDate(d);
}

export default function AuditPage() {
  const { hasPerm } = useAuth();
  const isAdmin = hasPerm("administrator");
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [people, setPeople] = useState([]);
  const [f, setF] = useState({ q: "", module: "", action: "", actor_id: "", date_from: "", date_to: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/audit?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/audit/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  const groups = [];
  let cur = null;
  rows.forEach((r) => {
    const lbl = r.when ? dayLabel(r.when) : "—";
    if (!cur || cur.label !== lbl) { cur = { label: lbl, items: [] }; groups.push(cur); }
    cur.items.push(r);
  });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
          <ListChecks size={28} weight="bold" /> Audit History
        </h1>
        <p className="font-body text-slate-600 text-sm mt-1">Append-only. Records cannot be edited or deleted.</p>
      </div>

      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-3">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="audit-search" placeholder="Search registration, asset, person, description…"
            value={f.q} onChange={(e) => set("q", e.target.value)} className="pl-10 h-11 border-2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          <select data-testid="audit-filter-module" className="h-10 border-2 border-slate-300 rounded px-2 text-sm w-full" value={f.module} onChange={(e) => set("module", e.target.value)}>
            <option value="">All modules</option>{modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="audit-filter-action" className="h-10 border-2 border-slate-300 rounded px-2 text-sm w-full" value={f.action} onChange={(e) => set("action", e.target.value)}>
            {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="audit-filter-person" className="h-10 border-2 border-slate-300 rounded px-2 text-sm w-full" value={f.actor_id} onChange={(e) => set("actor_id", e.target.value)}>
            <option value="">All people</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <div className="flex gap-1 min-w-0">
            <Input data-testid="audit-filter-from" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-10 border-2 text-xs px-1 min-w-0 flex-1" />
            <Input data-testid="audit-filter-to" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-10 border-2 text-xs px-1 min-w-0 flex-1" />
          </div>
        </div>
      </div>

      {groups.map((g) => (
        <div key={g.label} className="flex flex-col gap-2">
          <div className="font-body text-xs font-bold uppercase tracking-wide text-slate-400 mt-2">{g.label}</div>
          {g.items.map((r) => <AuditCard key={r.id} item={r} isAdmin={isAdmin} />)}
        </div>
      ))}
      {rows.length === 0 && <p className="text-slate-400">No audit entries match.</p>}
    </div>
  );
}
